import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { adminLogin, adminLogout, adminVerify } from '../utils/adminSession';
import { reportClientError } from '../utils/clientLogger';
import { AdminPanelLoginView } from '../components/admin/main/AdminPanelLoginView';
import { AdminPanelVendedoresTab } from '../components/admin/main/AdminPanelVendedoresTab';
import { AdminPanelLeadsTab } from '../components/admin/main/AdminPanelLeadsTab';
import { AdminPanelAgendamentosTab } from '../components/admin/main/AdminPanelAgendamentosTab';
import { AdminPanelUploadsTab } from '../components/admin/main/AdminPanelUploadsTab';
import { AdminPanelAddVendedorModal } from '../components/admin/main/AdminPanelAddVendedorModal';
import { adminPanelApiClient, fetchAdminPanelData, ADMIN_PANEL_TABS } from '../utils/adminPanelApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = window.location.origin;

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('vendedores');
  const [vendedores, setVendedores] = useState([]);
  const [leads, setLeads] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [metrics, setMetrics] = useState({ carros: 0, casas: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [showAddVendedor, setShowAddVendedor] = useState(false);
  const [vendedorForm, setVendedorForm] = useState({ nome: '', username: '', email: '', telefone: '', whatsapp: '' });
  const [copiedLink, setCopiedLink] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await adminVerify();
      setIsAuthenticated(response.data.valid);
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsChecking(false);
    }
  }, [adminVerify]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await adminLogin(password);
      if (response.data.success) {
        setIsAuthenticated(true);
      }
    } catch (err) {
      setLoginError('Senha incorreta');
    }
  };

  const handleLogout = () => {
    adminLogout().finally(() => setIsAuthenticated(false));
  };

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const { vendedores: vendedoresData, leadsMetrics, allLeads, contacts } = await fetchAdminPanelData();
      setVendedores(vendedoresData);
      setLeads(allLeads);
      setContactSubmissions(contacts);
      let totalCarros = 0, totalCasas = 0;
      vendedoresData.forEach(v => {
        totalCarros += v.leads_carros || 0;
        totalCasas += v.leads_casas || 0;
      });
      setMetrics({
        carros: totalCarros || leadsMetrics.total_leads || 0,
        casas: totalCasas || 0,
        total: (totalCarros + totalCasas) || leadsMetrics.total_leads || 0
      });
    } catch (err) {
      reportClientError('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchAdminPanelData, reportClientError]);

  const uploadFiles = useMemo(
    () => contactSubmissions.flatMap((contact) =>
      (contact.uploaded_files || []).map((file) => ({ ...file, contactId: contact.id, contactName: contact.name }))
    ),
    [contactSubmissions]
  );

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated, loadAllData]);

  const handleAddVendedor = async (e) => {
    e.preventDefault();
    try {
      await adminPanelApiClient.post(`${API}/vendedores`, {
        nome: vendedorForm.nome,
        telefone: vendedorForm.telefone,
        whatsapp: vendedorForm.whatsapp,
        email: vendedorForm.email,
        especialidade: 'todos'
      });
      setShowAddVendedor(false);
      setVendedorForm({ nome: '', username: '', email: '', telefone: '', whatsapp: '' });
      loadAllData();
    } catch (err) {
      alert('Erro ao adicionar vendedor');
    }
  };

  const handleToggleAtivo = async (vendedor) => {
    try {
      await adminPanelApiClient.put(`${API}/vendedores/${vendedor.id}`, { ativo: !vendedor.ativo });
      loadAllData();
    } catch (err) {
      alert('Erro ao atualizar status');
    }
  };

  const handleDeleteLead = async (lead) => {
    if (!window.confirm(`Excluir o lead de ${lead.cliente_nome}?`)) return;

    try {
      await adminPanelApiClient.delete(`${API}/admin/leads/${lead.id}`);
      await loadAllData();
    } catch (err) {
      alert('Erro ao excluir lead');
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Excluir o envio de ${contact.name}?`)) return;

    try {
      await adminPanelApiClient.delete(`${API}/admin/contacts/${contact.id}`);
      await loadAllData();
    } catch (err) {
      alert('Erro ao excluir envio');
    }
  };

  const copyLink = (type, codigo) => {
    const link = type === 'carros' 
      ? `${SITE_URL}/rh/${codigo}/carros`
      : `${SITE_URL}/rh/${codigo}/casas`;
    navigator.clipboard.writeText(link);
    setCopiedLink(`${type}-${codigo}`);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <AdminPanelLoginView
        password={password}
        setPassword={setPassword}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        loginError={loginError}
        handleLogin={handleLogin}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-600 hover:text-gray-900 flex items-center gap-1">
              ← Voltar
            </Link>
            <h1 className="text-xl font-bold text-gray-900">Painel Administrativo</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/questionario"
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-medium flex items-center gap-2 hover:from-purple-700 hover:to-purple-800 transition"
            >
              📝 Editor de Perguntas
            </Link>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900 transition"
            >
              Sair
            </button>
            <span className="text-gray-600 font-medium">RH Consulting Holdings</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl shadow-sm p-6 flex items-center gap-4">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
              <span className="text-3xl">🚗</span>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Leads de Carros</p>
              <p className="text-4xl font-bold text-gray-900">{metrics.carros}</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-6 flex items-center gap-4">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center">
              <span className="text-3xl">🏠</span>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Leads de Casas</p>
              <p className="text-4xl font-bold text-gray-900">{metrics.casas}</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-6 flex items-center gap-4">
            <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center">
              <span className="text-3xl">📊</span>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total de Leads</p>
              <p className="text-4xl font-bold text-gray-900">{metrics.total}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-8 border-b mb-6">
          {ADMIN_PANEL_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-3 px-1 border-b-2 transition ${
                activeTab === tab.id 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {activeTab === 'vendedores' && <AdminPanelVendedoresTab vendedores={vendedores} setShowAddVendedor={setShowAddVendedor} copyLink={copyLink} copiedLink={copiedLink} siteUrl={SITE_URL} handleToggleAtivo={handleToggleAtivo} />}
        {activeTab === 'leads' && <AdminPanelLeadsTab leads={leads} vendedores={vendedores} handleDeleteLead={handleDeleteLead} />}
        {activeTab === 'agendamentos' && <AdminPanelAgendamentosTab contactSubmissions={contactSubmissions} handleDeleteContact={handleDeleteContact} />}
        {activeTab === 'uploads' && <AdminPanelUploadsTab uploadFiles={uploadFiles} backendUrl={BACKEND_URL} />}
      </div>
      <AdminPanelAddVendedorModal isOpen={showAddVendedor} vendedorForm={vendedorForm} setVendedorForm={setVendedorForm} onClose={() => setShowAddVendedor(false)} onSubmit={handleAddVendedor} />
    </div>
  );
};

export default AdminPanel;
